// Platform-agnostic triage logic for the admin feedback inbox.
//
// The inbox UI is rendered twice — once on the legacy DOM web
// (apps/web) and once as a shared react-native component
// (@jackedlogs/ui, consumed by apps/mobile + apps/web-rnw). This
// module holds the parsing/sort/count/patch rules so both renderers
// agree and the behaviour is unit-testable without a DOM or a native
// runtime. Keep it free of React and any UI import.

import {
  Feedback,
  type Feedback as FeedbackT,
  type FeedbackPriority as FeedbackPriorityT,
  type FeedbackStatus as FeedbackStatusT,
} from "./schemas/feedback.js";

// The inbox filter is a status, or "all" for the unfiltered view.
export type FeedbackFilter = FeedbackStatusT | "all";

// Triage rank: lower sorts first. p0 (most urgent) → 0, untriaged
// (null priority) → last. Mirrors the inbox's "urgent floats up".
export const rankFeedbackPriority = (p: FeedbackPriorityT | null): number =>
  p === "p0" ? 0 : p === "p1" ? 1 : p === "p2" ? 2 : 3;

// Stable triage order: priority ascending (p0 first, null last), then
// most-recent first within a priority band. Pure — returns a new array.
export function sortFeedbackForTriage(
  items: readonly FeedbackT[],
): FeedbackT[] {
  return [...items].sort((a, b) => {
    const r = rankFeedbackPriority(a.priority) - rankFeedbackPriority(b.priority);
    if (r !== 0) return r;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

// Authoritative per-status counts (+ "all"), as the API returns them —
// computed on read from the whole partition (not by counting a fetched/
// paginated list, and not from a drift-prone cache). The client renders
// the filter chips from these so the numbers are always the true totals,
// independent of the active filter or page.
export type FeedbackCounts = Record<FeedbackFilter, number>;

// Parse the `counts` object off a `GET /api/feedback` response. Returns
// null when absent (older API / first paint) so the caller can fall back
// to counting the loaded list. Coerces each field to a finite number;
// `all` defaults to the sum when the server omits it.
export function parseFeedbackCounts(raw: unknown): FeedbackCounts | null {
  if (!raw || typeof raw !== "object") return null;
  const c = (raw as { counts?: unknown }).counts;
  if (!c || typeof c !== "object") return null;
  const obj = c as Record<string, unknown>;
  const num = (v: unknown): number =>
    typeof v === "number" && Number.isFinite(v) ? v : 0;
  const open = num(obj["open"]);
  const in_progress = num(obj["in_progress"]);
  const shipped = num(obj["shipped"]);
  const wontfix = num(obj["wontfix"]);
  const all =
    typeof obj["all"] === "number"
      ? num(obj["all"])
      : open + in_progress + shipped + wontfix;
  return { all, open, in_progress, shipped, wontfix };
}

// Per-status counts (+ "all") computed from a list — a fallback for when
// the server hasn't sent authoritative `counts` (older API / first paint).
// With pagination this only reflects the loaded page, so prefer the
// server counts (parseFeedbackCounts) whenever present.
export function countFeedbackByStatus(
  items: readonly FeedbackT[],
): Record<FeedbackFilter, number> {
  const out: Record<FeedbackFilter, number> = {
    all: items.length,
    open: 0,
    in_progress: 0,
    shipped: 0,
    wontfix: 0,
  };
  for (const it of items) out[it.status]++;
  return out;
}

// Apply a PATCH result to the visible list under the active filter.
// When the row's NEW status no longer matches a non-"all" filter, drop
// it (otherwise a "shipped" row lingers inside the "open" view and
// looks broken). Under "all", replace in place. Pure.
export function applyFeedbackPatch(
  prev: readonly FeedbackT[],
  updated: FeedbackT,
  filter: FeedbackFilter,
): FeedbackT[] {
  if (filter !== "all" && updated.status !== filter) {
    return prev.filter((it) => it.id !== updated.id);
  }
  return prev.map((it) => (it.id === updated.id ? updated : it));
}

// Parse the raw `GET /api/feedback` payload (`{ items: [...] }`) into a
// validated, triage-sorted list. Invalid rows are dropped silently so a
// single bad record never blanks the whole inbox.
export function parseFeedbackList(raw: unknown): FeedbackT[] {
  const items =
    raw &&
    typeof raw === "object" &&
    Array.isArray((raw as { items?: unknown }).items)
      ? (raw as { items: unknown[] }).items
      : [];
  const parsed: FeedbackT[] = [];
  for (const it of items) {
    const r = Feedback.safeParse(it);
    if (r.success) parsed.push(r.data);
  }
  return sortFeedbackForTriage(parsed);
}
